import type { AiResultData, SummaryPart } from "./types";

/** `[12:Parking permits]`. Titles cannot contain `]` or a newline. */
const MARKER = /\[(\d+):([^\]\n]+)\]/g;

/**
 * An unfinished marker at the very end of the text: `[`, `[12`, `[12:`,
 * `[12:Park`. Deliberately does not match `[abc`, which was never going to
 * become a marker and should render as the literal text it is.
 */
const TRAILING_PARTIAL = /\[\d*(?::[^\]\n]*)?$/;

/**
 * Splits summary text into renderable parts, resolving each marker against the
 * results received so far.
 *
 * @param streaming While true, a trailing unfinished marker is withheld. The
 * summary streams ahead of the excerpts it references, so without this a user
 * sees `see [12:Park` as plain text for a moment before it becomes a link.
 */
export function parseSummary(
  text: string,
  results: readonly AiResultData[],
  streaming = false,
): SummaryPart[] {
  if (!text) {
    return [];
  }

  let body = text;
  if (streaming) {
    const partial = TRAILING_PARTIAL.exec(body);
    if (partial) {
      body = body.slice(0, partial.index);
    }
  }
  if (!body) {
    return [];
  }

  const parts: SummaryPart[] = [];
  const marker = new RegExp(MARKER.source, "g");
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = marker.exec(body)) !== null) {
    if (match.index > cursor) {
      parts.push({ type: "text", text: body.slice(cursor, match.index) });
    }
    const [full, id, title] = match;
    const result = results.find((r) => r.id === id);
    parts.push({
      type: "link",
      id,
      title,
      permalink: result ? result.permalink : null,
    });
    cursor = match.index + full.length;
  }

  if (cursor < body.length) {
    parts.push({ type: "text", text: body.slice(cursor) });
  }

  return parts;
}
